import React, { useState } from 'react';
import { useAdminOps } from '../contexts/AdminOpsContext';
import { Badge } from './ui/badge';
import {
  Shield, AlertTriangle, CheckCircle2, XCircle, ChevronDown, ChevronUp,
  Activity, Users, Zap, Brain, Clock, TrendingUp, AlertCircle
} from 'lucide-react';

// Admin View indicator for the header
export function AdminViewBadge() {
  const { isAdmin, opsData } = useAdminOps();
  if (!isAdmin) return null;

  const health = opsData?.system_health;
  const hasIssues = health && (health.failed_publishes_24h > 0 || health.error_count > 0);

  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#D4AF37]/[0.08] border border-[#D4AF37]/15">
      <Shield size={12} className="text-[#D4AF37]" />
      <span className="text-[10px] font-medium text-[#D4AF37]">Admin</span>
      {hasIssues && (
        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
      )}
    </div>
  );
}

// System Health mini panel for Dashboard
export function SystemHealthPanel() {
  const { isAdmin, opsData, loading } = useAdminOps();
  if (!isAdmin || !opsData) return null;

  const h = opsData.system_health;
  const statusColor = h.status === 'critical' ? 'border-red-500/20 bg-red-500/[0.03]'
    : h.status === 'warning' ? 'border-amber-500/20 bg-amber-500/[0.03]'
    : 'border-emerald-500/20 bg-emerald-500/[0.03]';
  const statusIcon = h.status === 'critical' ? XCircle
    : h.status === 'warning' ? AlertTriangle : CheckCircle2;
  const StatusIcon = statusIcon;
  const statusText = h.status === 'critical' ? 'Critical' : h.status === 'warning' ? 'Warning' : 'Healthy';
  const statusTextColor = h.status === 'critical' ? 'text-red-400' : h.status === 'warning' ? 'text-amber-400' : 'text-emerald-400';

  return (
    <div className={`rounded-xl border p-4 ${statusColor} space-y-3`} data-testid="system-health-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield size={14} className="text-[#D4AF37]" />
          <span className="text-xs font-semibold">System Health</span>
        </div>
        <Badge className={`text-[9px] border gap-1 ${statusTextColor} bg-transparent border-current/20`}>
          <StatusIcon size={10} /> {statusText}
        </Badge>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: 'Failed (24h)', value: h.failed_publishes_24h, color: h.failed_publishes_24h > 0 ? 'text-red-400' : 'text-[#6B7280]' },
          { label: 'Partial (24h)', value: h.partial_publishes_24h, color: h.partial_publishes_24h > 0 ? 'text-amber-400' : 'text-[#6B7280]' },
          { label: 'Queue', value: h.scheduler_queue, color: 'text-sky-400' },
          { label: 'Errors', value: h.error_count, color: h.error_count > 0 ? 'text-red-400' : 'text-[#6B7280]' },
        ].map((s, i) => (
          <div key={i} className="p-2 rounded-lg bg-black/20 text-center">
            <p className={`text-lg font-bold tabular-nums ${s.color}`}>{s.value}</p>
            <p className="text-[9px] text-[#6B7280]">{s.label}</p>
          </div>
        ))}
      </div>
      {opsData.suspicious_users?.length > 0 && (
        <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-500/5 border border-amber-500/10">
          <AlertCircle size={13} className="text-amber-400 mt-0.5 shrink-0" />
          <p className="text-[10px] text-amber-300">
            {opsData.suspicious_users.length} suspicious user(s) detected (high activity)
          </p>
        </div>
      )}
    </div>
  );
}

// Collapsible admin section wrapper
export function AdminSection({ title, icon: Icon, children, defaultOpen = false }) {
  const { isAdmin } = useAdminOps();
  const [open, setOpen] = useState(defaultOpen);
  if (!isAdmin) return null;

  return (
    <div className="rounded-xl border border-[#D4AF37]/10 bg-[#D4AF37]/[0.02] overflow-hidden" data-testid="admin-section">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-[#D4AF37]/[0.04] transition-colors">
        <Shield size={12} className="text-[#D4AF37]" />
        {Icon && <Icon size={13} className="text-[#D4AF37]/60" />}
        <span className="text-[11px] font-medium text-[#D4AF37] flex-1">{title}</span>
        {open ? <ChevronUp size={12} className="text-[#D4AF37]/40" /> : <ChevronDown size={12} className="text-[#D4AF37]/40" />}
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 border-t border-[#D4AF37]/[0.06]">
          {children}
        </div>
      )}
    </div>
  );
}

// Operations panel for Admin page
export function OperationsPanel() {
  const { opsData, loading, refreshOps } = useAdminOps();
  if (!opsData) return null;

  return (
    <div className="space-y-4">
      {/* Recent Errors */}
      <div>
        <h4 className="text-xs font-semibold text-[#A9B4C0] mb-2 flex items-center gap-1.5">
          <XCircle size={12} className="text-red-400" /> API Errors ({opsData.recent_errors?.length || 0})
        </h4>
        {opsData.recent_errors?.length > 0 ? (
          <div className="space-y-1.5">
            {opsData.recent_errors.slice(0, 8).map((err, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-red-500/[0.03] border border-red-500/10">
                <div className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                <span className="text-[11px] text-[#A9B4C0] flex-1 truncate">
                  <span className="font-medium text-red-300">{err.channel}</span> — {err.error}
                </span>
                <span className="text-[9px] text-[#4B5563] shrink-0 tabular-nums">{err.date?.slice(11, 16)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-[#4B5563] p-3 text-center">No recent errors</p>
        )}
      </div>

      {/* Top AI Users */}
      <div>
        <h4 className="text-xs font-semibold text-[#A9B4C0] mb-2 flex items-center gap-1.5">
          <Brain size={12} className="text-[#D4AF37]" /> Top AI Users (7d)
        </h4>
        {opsData.top_ai_users?.length > 0 ? (
          <div className="space-y-1.5">
            {opsData.top_ai_users.slice(0, 5).map((u, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                <div className="w-6 h-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center text-[9px] font-bold text-[#D4AF37]">
                  {u.name?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium truncate">{u.name || u.email}</p>
                  <p className="text-[9px] text-[#4B5563]">{u.count} actions</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] font-bold tabular-nums text-[#D4AF37]">{u.total_spent}</p>
                  <p className="text-[9px] text-[#4B5563]">credits</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-[#4B5563] p-3 text-center">No AI usage data</p>
        )}
      </div>

      {/* AI Feature Usage */}
      <div>
        <h4 className="text-xs font-semibold text-[#A9B4C0] mb-2 flex items-center gap-1.5">
          <Activity size={12} className="text-[#00B8A9]" /> AI Feature Usage (7d)
        </h4>
        {opsData.ai_feature_usage?.length > 0 ? (
          <div className="space-y-1">
            {opsData.ai_feature_usage.map((f, i) => (
              <div key={i} className="flex items-center gap-2 p-2">
                <span className="text-[11px] text-[#A9B4C0] flex-1">{f.feature?.replace('ai_', '').replace(/_/g, ' ')}</span>
                <span className="text-[11px] font-bold tabular-nums text-[#00B8A9]">{f.count}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-[#4B5563] p-3 text-center">No data</p>
        )}
      </div>

      {/* Plan Distribution */}
      <div>
        <h4 className="text-xs font-semibold text-[#A9B4C0] mb-2 flex items-center gap-1.5">
          <TrendingUp size={12} className="text-emerald-400" /> Plan Distribution
        </h4>
        <div className="flex gap-2">
          {opsData.plan_distribution?.map((p, i) => (
            <div key={i} className="flex-1 p-2 rounded-lg bg-white/[0.02] border border-white/[0.04] text-center">
              <p className="text-lg font-bold tabular-nums">{p.count}</p>
              <p className="text-[9px] text-[#6B7280] capitalize">{p.plan}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
